// src/PaymentView.js
import React, { useState } from 'react';
import { callApi } from './api/api';

const PaymentView = ({ showPage, currentOrderId, currentOrderTotal, currentOrderStoreBillUrl }) => {
    const [status, setStatus] = useState('pending'); // pending | confirming | confirmed
    const [error, setError] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('UPI'); // 'UPI' | 'COD'
    const [upiTransactionId, setUpiTransactionId] = useState('');

    const upiId = "q524270108@ybl";
    const payeeName = "QwickMed";
    const amountStr = currentOrderTotal ? parseFloat(currentOrderTotal).toFixed(2) : "0.00";
    const upiLink = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&am=${amountStr}&cu=INR&tn=${encodeURIComponent('Order ' + currentOrderId)}`;

    if (!currentOrderId || currentOrderTotal === null) {
        return (
            <section className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-xl font-bold text-slate-700 mb-2">No Order Found</h2>
                <p className="text-slate-400 mb-6">Please go back and place your order again.</p>
                <button onClick={() => showPage('cart')}
                    className="bg-sky-500 text-white font-bold px-8 py-3 rounded-full hover:bg-sky-600 transition-colors">
                    Back to Cart
                </button>
            </section>
        );
    }

    /* ── ORDER CONFIRMED SCREEN ─────────────────────── */
    if (status === 'confirmed') {
        return (
            <section className="min-h-[60vh] flex items-center justify-center p-8">
                <div className="max-w-md w-full text-center">
                    {/* Success animation */}
                    <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-100">
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                            <i className="fas fa-check text-white text-3xl"></i>
                        </div>
                    </div>

                    <h1 className="text-3xl font-extrabold text-slate-800 mb-2">Order Confirmed!</h1>
                    <p className="text-slate-500 mb-6">Your order has been placed successfully. We'll deliver it to you soon.</p>

                    {/* Order details card */}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6 text-left space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Order ID</span>
                            <span className="font-bold text-slate-800 font-mono text-sm">#{currentOrderId}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Amount</span>
                            <span className="font-extrabold text-sky-600 text-lg">₹{parseFloat(currentOrderTotal).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Payment</span>
                            <span className="font-semibold text-slate-700 flex items-center gap-1">
                                {paymentMethod === 'UPI' ? (
                                    <><i className="fas fa-qrcode text-sky-500 text-xs"></i> UPI</>
                                ) : (
                                    <><i className="fas fa-money-bill-wave text-green-500 text-xs"></i> Cash on Delivery</>
                                )}
                            </span>
                        </div>
                        {paymentMethod === 'UPI' && upiTransactionId && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-500">Transaction ID</span>
                                <span className="font-mono text-slate-700 text-xs">{upiTransactionId}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Status</span>
                            <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full">Pending Delivery</span>
                        </div>
                    </div>

                    {/* Note */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left mb-6">
                        <p className="text-sm text-blue-700 font-semibold mb-1">📦 What happens next?</p>
                        <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                            <li>We will review and confirm your order shortly.</li>
                            <li>Our delivery partner will contact you before delivery.</li>
                            {paymentMethod === 'COD' && <li>Please keep the exact amount ready for COD payment.</li>}
                        </ul>
                    </div>

                    <div className="flex gap-3">
                        <button onClick={() => showPage('orders')}
                            className="flex-1 bg-white border-2 border-sky-400 text-sky-600 font-bold py-3 rounded-2xl hover:bg-sky-50 transition-colors">
                            Track Order
                        </button>
                        <button onClick={() => showPage('products')}
                            className="flex-1 bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 rounded-2xl transition-all shadow-lg shadow-sky-500/20">
                            Shop More
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    /* ── PAYMENT / CONFIRMATION SCREEN ──────────────── */
    return (
        <section className="max-w-lg mx-auto px-4 py-10">
            <div className="text-center mb-8">
                <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-lock text-sky-500 text-xl"></i>
                </div>
                <h1 className="text-2xl font-extrabold text-slate-800">Confirm Your Order</h1>
                <p className="text-slate-400 text-sm mt-1">Review your order before placing it</p>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-5">
                <h2 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                    <i className="fas fa-receipt text-sky-400 text-sm"></i> Order Summary
                </h2>
                <div className="space-y-3">
                    <div className="flex justify-between text-sm text-slate-600">
                        <span>Order ID</span>
                        <span className="font-mono font-bold text-slate-800">#{currentOrderId}</span>
                    </div>
                    <div className="border-t border-dashed border-slate-200 pt-3 flex justify-between items-center">
                        <span className="font-extrabold text-slate-800 text-lg">Total to Pay</span>
                        <span className="font-extrabold text-sky-600 text-2xl">₹{parseFloat(currentOrderTotal).toFixed(2)}</span>
                    </div>
                    <p className="text-[10px] text-slate-400 italic">* Final billed amount may slightly vary based on actual product pricing.</p>
                    
                    {currentOrderStoreBillUrl && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                            <a href={currentOrderStoreBillUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-xl text-sm transition-colors border border-emerald-200">
                                <i className="fas fa-file-invoice"></i> View Original Store Bill
                            </a>
                        </div>
                    )}
                </div>
            </div>

            {/* Payment Method Selection */}
            <div className="mb-6 space-y-3">
                {/* UPI Option */}
                <div 
                    onClick={() => setPaymentMethod('UPI')}
                    className={`bg-white rounded-2xl border-2 cursor-pointer shadow-sm p-4 transition-all ${paymentMethod === 'UPI' ? 'border-sky-500 bg-sky-50/50' : 'border-slate-200 hover:border-sky-200'}`}
                >
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-qrcode text-sky-600 text-xl"></i>
                        </div>
                        <div className="flex-1">
                            <p className="font-extrabold text-slate-800">Pay via UPI (GPay/PhonePe/Paytm)</p>
                            <p className="text-xs text-slate-500">Scan QR code and upload transaction ID</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'UPI' ? 'border-sky-500 bg-sky-500' : 'border-slate-300'}`}>
                            {paymentMethod === 'UPI' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                        </div>
                    </div>

                    {paymentMethod === 'UPI' && (
                        <div className="mt-4 border-t border-sky-100 pt-4 flex flex-col items-center">
                            
                            {/* Direct App Pay Button */}
                            <a 
                                href={upiLink} 
                                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-extrabold py-3.5 px-4 rounded-xl shadow-md transition-colors flex justify-center items-center gap-2 mb-6"
                            >
                                <i className="fas fa-mobile-alt text-xl"></i>
                                Pay Directly with UPI App
                            </a>

                            <div className="flex items-center gap-4 w-full mb-6">
                                <div className="h-px bg-slate-200 flex-1"></div>
                                <span className="text-xs font-bold text-slate-400 uppercase">OR Scan QR Code</span>
                                <div className="h-px bg-slate-200 flex-1"></div>
                            </div>

                            <img src="/upi-qr.jpg" alt="UPI QR Code" className="w-48 h-48 object-cover rounded-xl shadow-sm border border-slate-200 mb-3" />
                            <p className="text-sm font-bold text-slate-700 mb-1">{payeeName}</p>
                            <p className="text-xs text-slate-500 mb-4">Scan the QR code to pay <strong className="text-slate-800">₹{amountStr}</strong></p>
                            
                            <div className="w-full text-left">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Transaction ID (UTR) *</label>
                                <input 
                                    type="text" 
                                    value={upiTransactionId}
                                    onChange={(e) => setUpiTransactionId(e.target.value)}
                                    placeholder="Enter 12-digit UTR number" 
                                    className="w-full p-3 bg-white border border-sky-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm outline-none transition-all" 
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Please pay using the QR code first, then enter the Transaction ID here.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* COD Option */}
                <div 
                    onClick={() => setPaymentMethod('COD')}
                    className={`bg-white rounded-2xl border-2 cursor-pointer shadow-sm p-4 transition-all ${paymentMethod === 'COD' ? 'border-green-500 bg-green-50/50' : 'border-slate-200 hover:border-green-200'}`}
                >
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-hand-holding-usd text-green-600 text-xl"></i>
                        </div>
                        <div className="flex-1">
                            <p className="font-extrabold text-slate-800">Cash on Delivery</p>
                            <p className="text-xs text-slate-500">Pay in cash when your order is delivered.</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'COD' ? 'border-green-500 bg-green-500' : 'border-slate-300'}`}>
                            {paymentMethod === 'COD' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                        </div>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-600 font-medium">
                    {error}
                </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
                <button onClick={() => showPage('cart')} // TODO: might need better back navigation depending on if they came from cart or email
                    className="flex-1 bg-white border-2 border-slate-200 text-slate-600 font-bold py-3.5 rounded-2xl hover:border-slate-300 transition-colors">
                    Back
                </button>
                <button
                    onClick={async () => {
                        if (paymentMethod === 'UPI' && !upiTransactionId.trim()) {
                            setError('Please enter the UPI Transaction ID.');
                            return;
                        }
                        setStatus('confirming');
                        setError('');

                        try {
                            const res = await callApi(`/orders/${currentOrderId}/payment`, 'PUT', {
                                paymentMethod,
                                upiTransactionId
                            });

                            if (res.success) {
                                setStatus('confirmed');
                            } else {
                                setError(res.message || 'Failed to confirm payment.');
                                setStatus('pending');
                            }
                        } catch (err) {
                            setError('Server error. Please try again.');
                            setStatus('pending');
                        }
                    }}
                    disabled={status === 'confirming'}
                    className="flex-1 bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 text-white font-extrabold py-3.5 rounded-2xl transition-all shadow-lg shadow-sky-500/20 hover:-translate-y-0.5 flex items-center justify-center gap-2"
                >
                    {status === 'confirming' ? (
                        <><i className="fas fa-spinner fa-spin"></i> Confirming...</>
                    ) : (
                        <><i className="fas fa-check-circle text-sm"></i> Confirm Order</>
                    )}
                </button>
            </div>
        </section>
    );
};

export default PaymentView;
